import { useState } from "react";
import { SendEmailModal } from "pages/patient/PatientDocumentsPage/components/SendEmailModal/SendEmailModal";

import { AddLetterForm } from "components/AddLetterForm/AddLetterForm.jsx";
import { LettersTable } from "components/AddLetterForm/components/LettersTable/LettersTable";
import { useDownloadLetter } from "components/AddLetterForm/hooks/useDownloadLetter";
import { useActiveConsultationLetter } from "features/consultations/hooks/useActiveConsultationLetter";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { usePatientLetters } from "hooks/usePatientLetters";
import { toRows } from "utils/paginatedResponse";

import s from "./tabs.module.css";

const PATIENT_LETTERS_PAGE_SIZE = 200;

export const LetterTab = ({ consultationId, isReadOnly = false }) => {
  const patientId = useCurrentPatientId();
  const { data: patient } = usePatientData();
  const { data: staffInfo } = useStaffInfo();
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [emailModalLetter, setEmailModalLetter] = useState(null);

  const isStandalone = consultationId === null || consultationId === undefined;

  const { data: activeConsultationLetter, refetch: refetchConsultationLetters } =
    useActiveConsultationLetter(consultationId);

  /*
   * This tab shows every letter for the patient and has no paging control, so the hook's
   * default of ten would silently hide the eleventh once the rows were being read at all
   * (KIN-1896). Raising it is free here: the endpoint fetches the patient's whole list and
   * `getPaginationResponse` slices it in memory, so a larger page is the same query.
   *
   * A cap rather than no limit, because an unbounded page is how a screen falls over on the
   * one patient nobody tested with. If a practice ever passes it, this wants a real paging
   * control instead — the same one LettersSection already has.
   */
  const { data: allPatientLetters, refetch: refetchPatientLetters } =
    usePatientLetters(isStandalone ? patientId : null, {
      rows: PATIENT_LETTERS_PAGE_SIZE,
    });

  /*
   * Normalised through toRows because the two endpoints behind this tab answer in different
   * shapes for the same resource: the consultation one returns a bare list, the patient one
   * a pagination envelope. `?? []` did not catch the envelope — an object is neither null
   * nor undefined — so `letters.at(0)` threw on the pregnancy screens, which are the only
   * callers that pass no consultation id (KIN-1896).
   *
   * Applied to both sources rather than only the standalone one: a caller reading rows
   * should not depend on knowing which endpoint answered, and this pair is the evidence
   * that such knowledge goes stale.
   */
  const letters = isStandalone
    ? toRows(allPatientLetters)
    : toRows(activeConsultationLetter);
  const refetchLetters = isStandalone ? refetchPatientLetters : refetchConsultationLetters;
  const tableTitle = isStandalone ? "Patient Letters" : "Letters for this Consultation";

  const downloadLetter = useDownloadLetter();
  const tenantVal = localStorage.getItem("kinesin-tenant");

  if (isReadOnly && !letters.length) {
    return <LettersTable letters={[]} onEdit={() => {}} onDownload={() => {}} />;
  }

  const emailDocumentIds = emailModalLetter?.documentId
    ? String(emailModalLetter.documentId)
    : "";

  const emailDocuments = emailModalLetter?.documentId
    ? [
        {
          id: emailModalLetter.documentId,
          name: emailModalLetter.name || "Letter",
          documentType: "Letter",
        },
      ]
    : [];

  return (
    <>
      <div className={isReadOnly ? s.readOnlyContent : undefined}>
        <AddLetterForm
          key={selectedLetter?.id || letters.at(0)?.id || "new"}
          patient={patient}
          consultationId={consultationId}
          staffMemberId={staffInfo?.staffMember?.id}
          initialData={selectedLetter || letters.at(0) || undefined}
          autoSave={false}
          isReadOnly={isReadOnly}
          refetchLetters={refetchLetters}
          onEditLetter={(letter) => setSelectedLetter(letter)}
          onNewLetter={() => setSelectedLetter(null)}
        />
      </div>
      <div className={s.sectionWrapper}>
        <div className={s.sectionHeader}>
          <h3 className={s.sectionTitle}>{tableTitle}</h3>
        </div>
        <div className={s.sectionContent}>
          <LettersTable
            letters={letters}
            currentLetterId={selectedLetter?.id || letters.at(0)?.id}
            isReadOnly={isReadOnly}
            onEdit={(letter) => setSelectedLetter(letter)}
            onDownload={(letter) =>
              downloadLetter.mutate(
                { id: letter.id, tenantVal, patientId },
                { onSuccess: refetchLetters }
              )
            }
            onSendEmail={(letter) => setEmailModalLetter(letter)}
          />
        </div>
      </div>

      <SendEmailModal
        isOpen={!!emailModalLetter}
        onClose={() => setEmailModalLetter(null)}
        patient={patient}
        documentIds={emailDocumentIds}
        documents={emailDocuments}
      />
    </>
  );
};
